// AdminReceiptPanel.jsx - Admin dekont onay paneli
import React, { useState, useEffect } from 'react';
import { getPendingReceipts, approveReceipt, rejectReceipt } from '../../utils/newApi';
import Card from '../common/Card';
import Button from '../common/Button';

const AdminReceiptPanel = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isFakeReceipt, setIsFakeReceipt] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Dekontları yükle
  const loadReceipts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getPendingReceipts();
      setReceipts(data);
    } catch (err) {
      console.error('Error loading receipts:', err);
      setError(err.message || 'Dekontlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReceipts();
  }, []);

  // Dekont onayla
  const handleApprove = async (reservationId) => {
    if (!window.confirm('Bu dekontu onaylamak istediğinize emin misiniz?')) {
      return;
    }

    try {
      setProcessing(reservationId);
      setError('');
      setMessage('');

      const result = await approveReceipt(reservationId);
      setMessage(result.message || 'Dekont onaylandı.');

      // Listeyi güncelle
      await loadReceipts();
    } catch (err) {
      console.error('Error approving receipt:', err);
      setError(err.message || 'Dekont onaylanırken hata oluştu.');
    } finally {
      setProcessing(null);
    }
  };

  // Dekont reddet
  const handleReject = async (reservationId) => {
    if (!rejectionReason.trim()) {
      alert('Lütfen reddetme sebebini giriniz.');
      return;
    }

    const confirmMsg = isFakeReceipt
      ? 'Bu dekont sahte olarak işaretlenecek ve kullanıcıya CEZA verilecek. Emin misiniz?'
      : 'Bu dekontu reddetmek istediğinize emin misiniz?';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setProcessing(reservationId);
      setError('');
      setMessage('');

      const result = await rejectReceipt(reservationId, rejectionReason, isFakeReceipt);
      setMessage(result.message || 'Dekont reddedildi.');

      // Form sıfırla
      setSelectedReceipt(null);
      setRejectionReason('');
      setIsFakeReceipt(false);

      // Listeyi güncelle
      await loadReceipts();
    } catch (err) {
      console.error('Error rejecting receipt:', err);
      setError(err.message || 'Dekont reddedilirken hata oluştu.');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-200">Dekontlar yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <svg className="w-8 h-8 text-yellow-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h2 className="text-2xl font-bold text-white">Dekont Onay Paneli</h2>
        </div>
        <button
          onClick={loadReceipts}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Yenile
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Empty State */}
      {receipts.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-200 text-lg">Bekleyen dekont bulunmuyor.</p>
          </div>
        </Card>
      )}

      {/* Receipts List */}
      {receipts.map((receipt) => (
        <Card key={receipt.id} className="overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dekont Resmi */}
            <div>
              <h3 className="text-lg font-semibold text-blue-200 mb-3">Dekont Resmi</h3>
              <img
                src={receipt.receiptUrl}
                alt="Dekont"
                className="w-full rounded-lg border border-gray-600"
                onClick={() => window.open(receipt.receiptUrl, '_blank')}
                style={{ cursor: 'pointer' }}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">Büyütmek için tıklayın</p>
            </div>

            {/* Rezervasyon Bilgileri */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-blue-200 mb-3">Rezervasyon Bilgileri</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Kullanıcı:</span>
                    <span className="text-white">{receipt.userEmail || receipt.userName || `ID: ${receipt.userId?.substring(0, 8) || 'Bilinmiyor'}...`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Oda:</span>
                    <span className="text-white">{receipt.roomId === 'room-drum' ? 'Davul Odası' : receipt.roomId === 'room-piano' ? 'Piyano Odası' : receipt.roomId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Tarih:</span>
                    <span className="text-white">
                      {receipt.date?.toDate 
                        ? receipt.date.toDate().toLocaleDateString('tr-TR') 
                        : receipt.dateString 
                          ? new Date(receipt.dateString).toLocaleDateString('tr-TR')
                          : typeof receipt.date === 'string' 
                            ? new Date(receipt.date).toLocaleDateString('tr-TR')
                            : 'Bilinmiyor'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saat:</span>
                    <span className="text-white">{receipt.startTime} - {receipt.endTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Rezervasyon Tipi:</span>
                    <span className="text-white">
                      {receipt.reservationType === 'individual' ? 'Bireysel' : 'Grup'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Kişi Sayısı:</span>
                    <span className="text-gray-100">{receipt.numberOfPeople}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Toplam Ücret:</span>
                    <span className="text-gray-100 font-bold">{receipt.totalPrice} TL</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Yüklenme:</span>
                    <span className="text-gray-100">
                      {receipt.receiptUploadedAt?.toDate
                        ? receipt.receiptUploadedAt.toDate().toLocaleString('tr-TR')
                        : receipt.receiptUploadedAt
                        ? new Date(receipt.receiptUploadedAt).toLocaleString('tr-TR')
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {selectedReceipt === receipt.id ? (
                <div className="space-y-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-red-300">Dekont Reddetme</h4>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Reddetme sebebini giriniz..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                    rows={3}
                  />
                  <label className="flex items-center text-sm text-white">
                    <input
                      type="checkbox"
                      checked={isFakeReceipt}
                      onChange={(e) => setIsFakeReceipt(e.target.checked)}
                      className="mr-2"
                    />
                    Bu dekont sahte (Kullanıcıya ceza verilecek)
                  </label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReject(receipt.id)}
                      disabled={processing === receipt.id}
                      className="flex-1 bg-red-600 hover:bg-red-500"
                    >
                      {processing === receipt.id ? 'İşleniyor...' : 'Reddet'}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedReceipt(null);
                        setRejectionReason('');
                        setIsFakeReceipt(false);
                      }}
                      className="flex-1 bg-gray-600 hover:bg-gray-500"
                    >
                      İptal
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleApprove(receipt.id)}
                    disabled={processing === receipt.id}
                    className="flex-1 bg-green-600 hover:bg-green-500"
                  >
                    {processing === receipt.id ? (
                      <span className="flex items-center justify-center">
                        <div className="loader mr-2"></div>
                        İşleniyor...
                      </span>
                    ) : (
                      '✓ Onayla'
                    )}
                  </Button>
                  <Button
                    onClick={() => setSelectedReceipt(receipt.id)}
                    disabled={processing === receipt.id}
                    className="flex-1 bg-red-600 hover:bg-red-500"
                  >
                    ✗ Reddet
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AdminReceiptPanel;
